import { getFileExtensionFromUrl } from '@utils/general';

export const fileExtensionColorMap: Record<string, string> = {
  pdf: '#FF5454',
  xlsx: '#21A366',
  xls: '#21A366',
  xlsm: '#21A366',
  xlsb: '#21A366',
  xltx: '#21A366',
  xltm: '#21A366',
  docx: '#4E76A0',
  doc: '#4E76A0',
  jpeg: '#2b7cd3',
  jpg: '#2b7cd3',
  png: '#2b7cd3',
  zip: '#00343CF2',
  csv: '#8670C7',
};

export const fileExtensionSizeMap = {
  small: 16,
  middle: 24,
  large: 32,
  xlarge: 42,
} as const;

export type FileExtensionSize = keyof typeof fileExtensionSizeMap;

// Approximate advance width of a bold, uppercase sans-serif glyph relative to
// the font size. Used to predict how wide the rendered label will be so we can
// shrink the font until the full extension fits inside the fixed square box.
const GLYPH_WIDTH_RATIO = 0.62;
// Fraction of the box width the label is allowed to occupy (leaves breathing room).
const WIDTH_FILL_RATIO = 0.86;
// Smallest font we will ever render — keeps the label legible. Pathologically
// long extensions are additionally clipped by `overflow: hidden` in the box.
const MIN_FONT_SIZE = 5;

/** What can follow the last dot of a file name and still be its format. */
const EXTENSION_PATTERN = /^[a-z0-9]{1,5}$/;

/**
 * Returns the raw, lower-cased extension and the colour the badge should use.
 * Unknown extensions resolve to `color: undefined` so the caller can fall back.
 *
 * A NAME WITH NO EXTENSION HAS NO FORMAT. A document's display name is stored without
 * its extension, so a caller that only has the name used to get the whole name back as
 * the "extension" and the badge painted `ABACUS_KONTOBLAETTER_2023`, cropped, as the
 * file type. Only a short alphanumeric suffix after the last dot of the last path
 * segment counts; anything else is `''` and the badge stays a plain tile.
 */
export const resolveFileExtension = (document: string) => {
  const lastSegment = (document ?? '').split(/[?#]/)[0].split('/').pop() ?? '';
  const candidate = lastSegment.includes('.')
    ? getFileExtensionFromUrl(lastSegment).toLowerCase()
    : '';
  const extension = EXTENSION_PATTERN.test(candidate) ? candidate : '';
  return { extension, color: fileExtensionColorMap[extension] };
};

const baseFontSizeFor = (boxSize: number) =>
  boxSize >= 32 ? 10 : Math.max(MIN_FONT_SIZE, Math.round(boxSize / 3));

/**
 * Computes a font size that keeps the (full, never truncated) extension inside
 * the square badge for any box size and any extension length.
 */
export const getFileExtensionFontSize = (
  boxSize: number,
  extensionLength: number,
  override?: number,
): number => {
  if (override) return override;

  const safeLength = Math.max(extensionLength, 1);
  const usableWidth = boxSize * WIDTH_FILL_RATIO;
  const widthConstrained = Math.floor(usableWidth / (safeLength * GLYPH_WIDTH_RATIO));

  return Math.max(MIN_FONT_SIZE, Math.min(baseFontSizeFor(boxSize), widthConstrained));
};

export const getFileExtensionRadius = (boxSize: number) =>
  boxSize >= 32 ? 4 : Math.max(2, Math.round(boxSize / 8));

/**
 * Predicts the rendered label width (px) for a given font size — used by tests
 * to assert the label fits, and mirrors the math `getFileExtensionFontSize` uses.
 */
export const predictLabelWidth = (extensionLength: number, fontSize: number) =>
  extensionLength * GLYPH_WIDTH_RATIO * fontSize;

export const maxLabelWidth = (boxSize: number) => boxSize * WIDTH_FILL_RATIO;
